import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Save, ChevronDown, HardDrive, Cloud } from 'lucide-react';
import { backupStore } from '../../../stores/backup.store';
import { Button } from '../../design/Button';
import { Select } from '../../design/Select';
import ToggleSwitch from '../../common/ToggleSwitch';

/** „Jetzt sichern" – mit einem Klick ein Backup, Optionen im aufklappbaren Menü. */
export const ManualBackupMenu = observer(({ ignoredFiles }: { ignoredFiles?: string }) => {
  const [open, setOpen] = useState(false);
  const [hdd, setHdd] = useState(true);
  const [cloud, setCloud] = useState(false);
  const [level, setLevel] = useState('1');
  const [skipIgnored, setSkipIgnored] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const busy = backupStore.backupStatus !== 'idle';

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const start = () => {
    setOpen(false);
    backupStore.handleManualBackup(parseInt(level, 10), !!ignoredFiles && skipIgnored, hdd, cloud);
  };

  return (
    <div ref={ref} className="relative">
      <div className="inline-flex rounded-lg overflow-hidden">
        <Button onClick={start} disabled={busy || (!hdd && !cloud)} className="rounded-r-none">
          <Save size={15} /> {busy ? 'Backup läuft …' : 'Jetzt sichern'}
        </Button>
        <Button onClick={() => setOpen((o) => !o)} disabled={busy} className="rounded-l-none border-l border-ink/30 px-2.5" aria-label="Backup-Optionen">
          <ChevronDown size={15} />
        </Button>
      </div>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 z-30 rounded-xl border border-line bg-surface-2 shadow-2xl p-4 space-y-4 animate-in fade-in zoom-in-95 duration-150">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted font-medium">Wohin sichern?</div>
          <label className="flex items-center justify-between gap-3 text-sm text-fg">
            <span className="inline-flex items-center gap-2"><HardDrive size={14} className="text-fg-muted" /> HDD (schnell)</span>
            <ToggleSwitch enabled={hdd} onChange={setHdd} />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm text-fg">
            <span className="inline-flex items-center gap-2"><Cloud size={14} className="text-fg-muted" /> Google Drive</span>
            <ToggleSwitch enabled={cloud} onChange={setCloud} />
          </label>
          {cloud && (
            <Select
              label="Kompression"
              value={level}
              onChange={setLevel}
              options={[
                { value: '0', label: 'Keine (am schnellsten)' },
                { value: '1', label: 'Schnell' },
                { value: '6', label: 'Normal' },
                { value: '9', label: 'Stark (am kleinsten)' },
              ]}
            />
          )}
          {ignoredFiles && (
            <label className="flex items-center justify-between gap-3 text-xs text-fg-muted">
              <span>Ausgeschlossene Ordner weglassen ({ignoredFiles})</span>
              <ToggleSwitch enabled={skipIgnored} onChange={setSkipIgnored} />
            </label>
          )}
          <Button className="w-full" onClick={start} disabled={!hdd && !cloud}>
            <Save size={15} /> Backup starten
          </Button>
        </div>
      )}
    </div>
  );
});
